/**
 * v2 API contract of «Состав» (docs/research/design2-shell-capture.md §5): a timeline of
 * assignments by person or by project over a window of months.
 */
import { z } from 'zod';
import { isPeriod } from '../dates';

/** Months shown at once (design N). */
export const ROSTER_MONTHS = 8;
/** The window starts this many months before the current one («Сегодня»). */
export const ROSTER_LEAD_MONTHS = 4;
/** ‹ / › move the window by this many months. */
export const ROSTER_STEP_MONTHS = 3;
/** Upcoming chips look this many days ahead (terms changes have no limit). */
export const ROSTER_UPCOMING_DAYS = 60;
export const ROSTER_UPCOMING_MAX = 5;

export const ROSTER_BY = ['person', 'project'] as const;
export type RosterBy = (typeof ROSTER_BY)[number];

const MAX_WINDOW_MONTHS = 24;

export const rosterQuerySchema = z.object({
  from: z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ').optional(),
  months: z.coerce.number().int().min(1).max(MAX_WINDOW_MONTHS).default(ROSTER_MONTHS),
  by: z.enum(ROSTER_BY).default('person'),
});
export type RosterQuery = z.infer<typeof rosterQuerySchema>;

/** Bar look: no rate (dashed amber), future (dashed), past (grey), current (navy tint). */
export type RosterBarState = 'norate' | 'future' | 'past' | 'current';

export interface RosterBar {
  readonly assignmentId: string;
  readonly personId: string;
  readonly personName: string;
  readonly projectId: string;
  readonly projectName: string;
  readonly label: string;
  readonly from: string | null;
  readonly to: string | null;
  readonly lane: number;
  readonly state: RosterBarState;
  /** Ended before today (greyed text even without a rate). */
  readonly isPast: boolean;
  /** «26 $/ч · своя», «2 000 $/мес» or «нет ставки». */
  readonly sub: string;
  /** Three-line tooltip. */
  readonly title: string;
  readonly menuTitle: string;
  readonly menuSub: string;
}

export interface RosterRow {
  readonly kind: RosterBy;
  readonly id: string;
  readonly label: string;
  readonly terms: string | null;
  readonly extra: string | null;
  /** «нет условий» / «нет ставки клиенту». */
  readonly missing: string | null;
  /** Dimmed rows: people who left, paused projects. */
  readonly dim: 'left' | 'paused' | null;
  readonly lanes: number;
  readonly bars: readonly RosterBar[];
  readonly emptyTitle: string;
}

/** GET /api/v1/roster */
export interface RosterResponse {
  readonly today: string;
  readonly currentPeriod: string;
  readonly by: RosterBy;
  readonly months: readonly string[];
  /** First and last day of the window. */
  readonly start: string;
  readonly end: string;
  readonly activeToday: number;
  readonly upcoming: readonly string[];
  readonly rows: readonly RosterRow[];
}
